/* eslint-disable react/jsx-pascal-case */
import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Modal,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import NavBar from "../../../components/navbar/navbar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import swal from "sweetalert";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { Banner, BannerUpdateStatusResponse } from "./types";
import { ModalContainer } from "../styles";
import { VisuallyHiddenInput } from "./styles";
import { API_URL, sentActionLog, verify } from "../../../utils/common";

const Carrusel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [data, setData] = React.useState<Banner[]>([]);
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = React.useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = React.useState<string>("");
  const [file, setFile] = React.useState<File | undefined>(undefined);

  const columns = React.useMemo<MRT_ColumnDef<Banner>[]>(
    () => [
      {
        accessorKey: "fileName",
        header: "Nombre del Archivo",
        size: 250,
      },
      {
        accessorKey: "url",
        header: "Vista Previa",
        size: 150,
        Cell: ({ row }) => (
          <IconButton
            color="secondary"
            onClick={() => handlePreview(row.original.url)}
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "activo",
        header: "Estatus",
        size: 120,
        Cell: ({ row }) => (
          <Chip
            label={row.original.activo ? "Activo" : "Inactivo"}
            color={row.original.activo ? "success" : "default"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "existsInS3",
        header: "En S3",
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={row.original.existsInS3 ? "Sí" : "No"}
            color={row.original.existsInS3 ? "success" : "error"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Fecha de Creación",
        size: 180,
        Cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleString("es-MX"),
      },
    ],
    []
  );

  const getBanners = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    setIsLoading(true);
    fetch(`${API_URL}/carrusel/downloads/banners`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: Banner[]) => {
        setIsLoading(false);
        setData(response);
        setRowCount(response.length);
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        swal({
          title: "Error",
          text: "Error al cargar los banners",
          icon: "error",
        });
      });
  }, []);

  const handlePreview = (url: string) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleToggleStatus = (banner: Banner) => {
    const token = localStorage.getItem("jwt");
    const newStatus = !banner.activo;
    
    // Obtener el usuario de red del localStorage
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const networkUser = user?.networkUser || user?.username || null;  // ← CORREGIDO: String

    fetch(`${API_URL}/carrusel/status/${banner.fileName}`, {
      method: "PATCH",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({ 
        activo: newStatus,
        updatedBy: networkUser  // ← Enviando el usuario de red (String)
      }),
    })
      .then((res) => res.json())
      .then((response: BannerUpdateStatusResponse) => {
        swal({
          title: "Actualizado!",
          text: `El banner ahora está ${newStatus ? "activo" : "inactivo"}`,
          icon: "success",
        });
        getBanners();
        sentActionLog(
          "Update_Banner_Status",
          `Cambió estatus del banner: ${banner.fileName} a ${newStatus ? "activo" : "inactivo"}`
        );
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: "No se pudo actualizar el estatus",
          icon: "error",
        });
      });
  };

  const handleDelete = (row: Banner) => {
    swal({
      title: "¿Estás seguro?",
      text: `Se eliminará el banner: ${row.fileName}`,
      icon: "warning",
      buttons: ["Cancelar", "Eliminar"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        const token = localStorage.getItem("jwt");
        fetch(`${API_URL}/carrusel/delete/carrusel/${row.fileName}`, {
          method: "DELETE",
          headers: {
            Authorization: `bearer ${token}`,
            "content-type": "application/json",
          },
        })
          .then((res) => res.ok)
          .then((response: boolean) => {
            if (response) {
              swal({
                title: "Eliminado!",
                text: "Se ha eliminado correctamente! :)",
                icon: "success",
              });
              getBanners();
              sentActionLog("Delete_Banner", `Elimino un Banner: ${row.fileName}`);
            }
          })
          .catch((err) => {
            console.error(err);
            swal({
              title: "Error",
              text: "No se pudo eliminar el banner",
              icon: "error",
            });
          });
      }
    });
  };

  const handleSave = () => {
    if (file?.name) {
      const token = localStorage.getItem("jwt");
      const data = {
        fileName: file.name,
        metadata: {
          title: file.name.split(".")[0],
        },
      };
      fetch(`${API_URL}/carrusel/upload/banners`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          Authorization: `bearer ${token}`,
          "content-type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((response: { [key: string]: string }) => {
          if (response[file.name]) {
            fetch(response[file.name], {
              method: "PUT",
              body: file,
              headers: {
                "x-amz-meta-title": file.name.split(".")[0],
              },
            })
              .then((res) => res.ok)
              .then((response: boolean) => {
                if (response) {
                  swal({
                    title: "Guardado!",
                    text: "Se ha guardado correctamente el banner",
                    icon: "success",
                  });
                  setOpen(false);
                  getBanners();
                  sentActionLog("Create_Banner", `Creo un nuevo Banner: ${file.name}`);
                } else {
                  swal({
                    title: "Error :(",
                    text: "Hubo un error favor de contactar a soporte",
                    icon: "error",
                  });
                }
              })
              .catch((err) => {
                swal({
                  title: "Error :(",
                  text: JSON.stringify(err),
                  icon: "error",
                });
                console.error(err);
              });
          } else {
            swal({
              title: "Error :(",
              text: "Hubo un error favor de contactar a soporte",
              icon: "error",
            });
          }
        })
        .catch((err) => {
          swal({ title: "Error :(", text: JSON.stringify(err), icon: "error" });
          console.error(err);
        });
      setFile(undefined);
    } else {
      swal({
        title: "Ooops :(",
        text: "Selecciona una Imagen",
        icon: "warning",
      });
    }
  };

  const handleClose = () => {
    setFile(undefined);
    setOpen(false);
  };

  const handleClosePreview = () => {
    setPreviewUrl("");
    setPreviewOpen(false);
  };

  React.useEffect(() => {
    (async () => {
      if (!["/", "/login"].includes(location.pathname)) {
        const currentToken = localStorage.getItem("jwt");
        if (currentToken) {
          const isValid = await verify(currentToken);
          if (!isValid) {
            navigate("/");
          } else {
            const permissions = JSON.parse(
              localStorage.getItem("user") || "{}"
            );
            if (!permissions?.administrationAccess) {
              navigate("/Home");
            }
          }
        } else {
          navigate("/");
        }
      }
    })();
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    getBanners();
    sentActionLog("Visit_Banners", "Entró en la vista de Banners");
  }, [getBanners]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableFilters: true,
    enableHiding: false,
    enableSorting: true,
    enableColumnActions: true,
    enablePagination: true,
    enableRowActions: true,
    manualPagination: false,
    rowCount,
    positionActionsColumn: "last",
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Tooltip title="Agregar nuevo banner">
          <IconButton onClick={() => setOpen(true)}>
            <AddIcon />
          </IconButton>
        </Tooltip>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px", alignItems: "center" }}>
        <Tooltip title={row.original.activo ? "Desactivar" : "Activar"}>
          <Switch
            checked={row.original.activo}
            onChange={() => handleToggleStatus(row.original)}
            color="secondary"
            size="small"
          />
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton color="error" onClick={() => handleDelete(row.original)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    state: {
      isLoading,
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        "& td": {
          "&::after": {
            backgroundColor: "#f5f5f5!important",
          },
        },
        "&:hover": {
          "& td": {
            "&::after": {
              backgroundColor: "#0000004d!important",
            },
          },
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        "& .MuiButton-root": {
          backgroundColor: "#F70083",
        },
      },
    },
  });

  return (
    <Box width={"100%"} mb={8}>
      <NavBar />
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" mb={2}>
            Gestión de Carrusel Home
          </Typography>
          <MaterialReactTable table={table} />
        </CardContent>
      </Card>

      {/* Modal para subir nuevo banner */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Upload-Banner-Modal"
      >
        <ModalContainer theme={theme}>
          <Box display={"flex"} flexDirection="row-reverse">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={4} pt={0}>
            <Typography mb={2} variant="h5" fontWeight="bold">
              Subir Banner al Carrusel
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Formatos soportados: JPG, PNG, SVG
            </Typography>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              color="secondary"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              Seleccionar archivo
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={(event) => setFile(event.target.files?.[0])}
              />
            </Button>
            {file ? (
              <Box mt={2}>
                <Typography variant="body2">
                  <strong>Archivo seleccionado:</strong> {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tamaño: {(file.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                No se ha seleccionado ningún archivo
              </Typography>
            )}
            <Button
              style={{ marginTop: theme.spacing(3) }}
              variant="contained"
              color="secondary"
              onClick={handleSave}
              fullWidth
              disabled={!file}
            >
              Guardar Banner
            </Button>
          </Box>
        </ModalContainer>
      </Modal>

      {/* Modal para vista previa de imagen */}
      <Modal
        open={previewOpen}
        onClose={handleClosePreview}
        aria-labelledby="Preview-Image-Modal"
      >
        <ModalContainer theme={theme}>
          <Box display={"flex"} flexDirection="row-reverse">
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={2}>
            <Typography mb={2} variant="h6" fontWeight="bold">
              Vista Previa
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxHeight: "500px",
                overflow: "auto",
              }}
            >
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "500px",
                  borderRadius: "8px",
                }}
              />
            </Box>
          </Box>
        </ModalContainer>
      </Modal>
    </Box>
  );
};

export default Carrusel;